// packages/observability/src/sampling-span-processor.ts
//
// SpanProcessor that drops fast-and-successful spans at `onEnd`. Replaces the
// previous "never call .end()" hack in spans.ts which was fragile (depended
// on SDK GC behaviour) and broke parent/child relationships when a child was
// kept but its parent dropped. With this processor, every span is always
// ended properly; the kept-vs-dropped decision is taken here, with the parent
// context intact. The downstream BatchSpanProcessor / exporter only sees the
// spans we keep.

import { SpanStatusCode } from "@opentelemetry/api";
import type { Context } from "@opentelemetry/api";
import type {
  ReadableSpan,
  Span,
  SpanProcessor,
} from "@opentelemetry/sdk-trace-base";

export interface AttentionOnlySpanProcessorOptions {
  /** Slowness threshold in ms. Spans whose `duration_ms` attribute (or
   *  measured wall time) exceeds this are kept. Default 1000. */
  slowMs?: number;
  /** Inner processors to forward kept spans to (e.g. a BatchSpanProcessor). */
  inner?: SpanProcessor[];
}

export class AttentionOnlySpanProcessor implements SpanProcessor {
  private readonly slowMs: number;
  private readonly inner: SpanProcessor[];

  constructor(opts: AttentionOnlySpanProcessorOptions = {}) {
    this.slowMs = opts.slowMs ?? 1000;
    this.inner = opts.inner ?? [];
  }

  onStart(span: Span, parentContext: Context): void {
    for (const p of this.inner) {
      p.onStart(span, parentContext);
    }
  }

  onEnd(span: ReadableSpan): void {
    if (!this.shouldKeep(span)) {
      return;
    }
    for (const p of this.inner) {
      p.onEnd(span);
    }
  }

  private shouldKeep(span: ReadableSpan): boolean {
    if (span.status.code === SpanStatusCode.ERROR) {
      return true;
    }
    if (span.attributes.incident === true) {
      return true;
    }

    const tagged = span.attributes.duration_ms;
    const perSpanSlowMs = span.attributes["atriam.slow_ms"];
    const threshold =
      typeof perSpanSlowMs === "number" ? perSpanSlowMs : this.slowMs;
    if (typeof tagged === "number" && tagged >= threshold) {
      return true;
    }

    // Fall back to measured duration (hrTime tuple → ms).
    const [s0, n0] = span.startTime;
    const [s1, n1] = span.endTime;
    const elapsedMs = (s1 - s0) * 1000 + (n1 - n0) / 1e6;
    return elapsedMs >= threshold;
  }

  async forceFlush(): Promise<void> {
    await Promise.all(this.inner.map((p) => p.forceFlush()));
  }

  async shutdown(): Promise<void> {
    await Promise.all(this.inner.map((p) => p.shutdown()));
  }
}
